/**
 * MortgageLeadFormHandler Module
 * This module handles the display and functionality of the mortgage lead form.
 * It creates a modal for the lead form, binds event listeners for closing and submitting,
 * and manages the display based on device type (mobile or desktop).
 */
(function mortgageLeadFormHandler(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  const leadFormTemplate = document.querySelector('[data-mortgage-lead-form-template]');
  const thankYouTemplate = document.querySelector('[data-mortgage-thank-you-template]');
  const isMobileDevice = !!document.querySelector('[data-ismobiledevice="true"]');
  const isUserLoggedIn = () => !!document.querySelector('[data-isloggedin="True"]');

  if (!leadFormTemplate || !thankYouTemplate) {
    return;
  }

  const previousMortgageLeadFormHandler = global.NewHomeSource.MortgageLeadFormHandler;

  /**
   * Creates a modal element for the mortgage lead form by cloning the 
   * inner HTML of the lead form template. This function ensures that 
   * the modal is properly structured and ready for display.
   *
   * @function getModal
   * @returns {HTMLElement} The modal element containing the mortgage lead form.
   */
  const getModal = (template) => {
    const container = document.createElement('div');
    container.innerHTML = template.innerHTML.trim();

    return container.firstElementChild;
  }

  /**
   * Applies a phone number mask to the input field within the mortgage modal.
   * This function uses the vanillaMasker library to format the phone input
   * according to the specified pattern. The pattern used is (999) 999-9999,
   * which formats the input as a standard US phone number.
   *
   * @function applyMaskToPhoneInput
   * @param {Function} vanillaMasker - The masking library function used to apply the mask.
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const applyMaskToPhoneInput = (vanillaMasker, mortgageModal) => {
    if (vanillaMasker && mortgageModal) {
      const phoneInput = mortgageModal.querySelector('[data-phone-input]');
      vanillaMasker(phoneInput).maskPattern('(999) 999-9999');
    }
  }

  /**
   * Removes the error message styling from the specified input field.
   * This function locates the closest field container for the given input field
   * and removes the error class, indicating that the field is no longer in an error state.
   *
   * @function removeErrorMessage
   * @param {HTMLElement} field - The input field from which to remove the error message.
   * @returns {void}
   */
  const removeErrorMessage = (field) => {
    const fieldContainer = field.closest('[data-form-field-container]');
    if (!fieldContainer) {
      return;
    }

    fieldContainer.classList.remove('nhs-c-input__wrapper--error');
  }

  /**
   * Handles the change event for the "lives outside of the US" input.
   * This function enables or disables the zip code input field based on
   * whether the user indicates they live outside of the US. If the user
   * selects that they live outside of the US, the zip code input is disabled,
   * cleared, and marked as not required. If they do not live outside of the US,
   * the zip code input is enabled and marked as required.
   *
   * @function onOutsideOfUsClicked
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const onOutsideOfUsClicked = (mortgageModal) => {
    const zipCodeInput = mortgageModal.querySelector('[data-zip-code-input]');
    const livesOutsideOfUsInput = mortgageModal.querySelector('[data-outside-of-us]');

    if (livesOutsideOfUsInput.checked) {
      zipCodeInput.disabled = true;
      zipCodeInput.value = '';
      zipCodeInput.removeAttribute('required');
      zipCodeInput.removeAttribute('aria-invalid');
      removeErrorMessage(zipCodeInput);
      return;
    }

    zipCodeInput.disabled = false;
    zipCodeInput.setAttribute('required', '');
  }

  /**
   * Displays the mortgage lead form modal for mobile users.
   * This function applies the necessary CSS for the modal, adds the modal
   * to the document body, and prevents background scrolling by adding a 
   * specific class to the body element.
   *
   * @function showMobileForm
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const showMobileForm = (mortgageModal) => {
    const modalCss = document.querySelector('#modalCss');
    if (modalCss && global.loadCSS) {
      global.loadCSS(modalCss.value);
    }

    mortgageModal.classList.add('nhs__modal');
    document.body.appendChild(mortgageModal);

    require(["vanillaMasker"], (vanillaMasker) => {
      applyMaskToPhoneInput(vanillaMasker, mortgageModal);
    });
  }

  /**
   * Removes the 'data-is-modal-loaded' attribute from the specified modal trigger element.
   * This function is used to reset the state of the modal trigger after the modal has been displayed.
   *
   * @function removeLoadedAttributeFromModalTrigger
   * @param {HTMLElement} modalTrigger - The element that triggered the modal display.
   * @returns {void}
   */
  const removeLoadedAttributeFromModalTrigger = (modalTrigger) => {
    modalTrigger.removeAttribute('data-is-modal-loaded');
  }

  /**
   * Displays the mortgage lead form modal for desktop users.
   * This function creates a modal using the provided mortgageModal element
   * and the leadFormTemplate. It utilizes the ModalLoader to handle the
   * display of the modal on desktop devices.
   *
   * @function showDesktopForm
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const showDesktopForm = (mortgageModal) => {
    global.NewHomeSource.ModalLoader.createModal(mortgageModal);
    const modalTriggers = document.querySelectorAll('[data-is-modal-loaded]');
    modalTriggers.forEach(removeLoadedAttributeFromModalTrigger);
    applyMaskToPhoneInput(global.VMasker, mortgageModal);
  }

  /**
   * Closes the mortgage modal and restores scroll functionality to the body.
   * This function removes the modal from the DOM and ensures that the body
   * can scroll again by removing the 'nhs-u-overflow--hidden' class.
   *
   * @function closeModal
   * @param {HTMLElement} mortgageModal - The modal element to be closed.
   * @returns {void}
   */
  const closeModal = (mortgageModal) => {
    document.body.classList.remove('nhs-u-overflow--hidden');
    mortgageModal.remove();

    if (global.NewHomeSource && global.NewHomeSource.ModalLoader) {
      NewHomeSource.ModalLoader.resetIsModalLoadedFlag();
      NewHomeSource.ModalLoader.hideModal();
    }
  }

  /**
   * Creates a new account for the user after closing the mortgage modal.
   * This function first closes the modal and then checks if the signIn 
   * method from the auth0Dom object is available. If it is, the function 
   * calls the signIn method to initiate the account creation process.
   *
   * @function createAccount
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const createAccount = (mortgageModal) => {
    closeModal(mortgageModal);
    if (!!global.auth0Dom && !!global.auth0Dom.signIn) {
      global.auth0Dom.signIn();
    }
  }

  /**
   * Toggles the display of the second legal consent text in the mortgage modal.
   * This function checks the visibility state of the second consent text and
   * toggles the 'hidden' attribute accordingly. It is typically used to show
   * or hide additional legal information based on user interaction.
   *
   * @function toggleLegalConsentVisibleText
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const toggleLegalConsentVisibleText = (mortgageModal) => {
    const secondConsent = mortgageModal.querySelector("[data-second-legal-consent-text]");
    secondConsent.toggleAttribute("hidden");
  }

  /**
   * Toggles the display of error messages for each input field in the mortgage lead form.
   * This function checks each input field for the 'aria-invalid' attribute and updates
   * the corresponding field container's class to indicate an error state.
   * data-error-text
   * @function toggleErrorMessages
   * @param {HTMLElement} form - The form element containing the input fields to validate.
   * @returns {void}
   */
  const toggleErrorMessages = (form) => {
    const allFields = form.querySelectorAll('input');
    allFields.forEach((field) => {
      const fieldContainer = field.closest('[data-form-field-container]');
      if (!fieldContainer) return;

      if (field.hasAttribute('aria-invalid')) {
        fieldContainer.classList.add('nhs-c-input__wrapper--error');
        return;
      }

      fieldContainer.classList.remove('nhs-c-input__wrapper--error');
    });
  };

  /**
   * Binds event listeners to the mortgage modal's close and submit buttons.
   * The close button will trigger the closeModal function, removing the modal
   * from the DOM and restoring scroll functionality to the body.
   * The submit button will trigger the submitForm function, which currently
   * displays an alert indicating the form has been submitted.
   *
   * @function bindEvents
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {void}
   */
  const bindEvents = (mortgageModal) => {
    const closeButton = mortgageModal.querySelector('[data-modal-close-element]');
    if (closeButton) {
      closeButton.addEventListener('click', closeModal.bind(this, mortgageModal));
    }

    const submitButton = mortgageModal.querySelector('[data-mortgage-cta-button]');
    if (submitButton) {
      submitButton.addEventListener('click', submitForm.bind(this, mortgageModal));
    }

    const outsideOfUsCheckbox = mortgageModal.querySelector('[data-outside-of-us]');
    if (outsideOfUsCheckbox) {
      outsideOfUsCheckbox.addEventListener('change', onOutsideOfUsClicked.bind(this, mortgageModal));
    }

    const createAccountButton = mortgageModal.querySelector('[data-create-account-button]');
    if (createAccountButton) {
      createAccountButton.addEventListener('click', createAccount.bind(this, mortgageModal));
    }

    const legalConsentToggleCheckBox = mortgageModal.querySelector('#read-more-chk-mortgage');
    if (legalConsentToggleCheckBox) {
      legalConsentToggleCheckBox.addEventListener("change", toggleLegalConsentVisibleText.bind(this, mortgageModal));
    }

    document.addEventListener('modalClosed', (event) => {
      if (event.detail &&
        event.detail.modal &&
        event.detail.modal.querySelector('[data-mortgage-modal]')) {
        document.body.classList.remove('nhs-u-overflow--hidden');
      }
    });
  }

  /**
 * Displays the thank you screen for mobile users after form submission.
 * This function adds the necessary CSS class to the thank you modal,
 * appends it to the document body, and prevents background scrolling
 * by adding a specific class to the body element.
 *
 * @function showMobileThankYouScreen
 * @param {HTMLElement} thankYouModal - The modal element containing the thank you message.
 * @returns {void}
 */
  const showMobileThankYouScreen = (thankYouModal) => {
    thankYouModal.classList.add('nhs__modal');
    document.body.appendChild(thankYouModal);
  }

  /**
   * Displays the thank you screen for desktop users after form submission.
   * This function creates a modal using the provided mortgageModal element
   * and utilizes the ModalLoader to handle the display of the thank you screen.
   * It also resets the state of any modal triggers that were used to open the modal.
   *
   * @function showDesktopThankYouScreen
   * @param {HTMLElement} mortgageModal - The modal element containing the thank you message.
   * @returns {void}
   */
  const showDesktopThankYouScreen = (mortgageModal) => {
    global.NewHomeSource.ModalLoader.createModal(mortgageModal);
    const modalTriggers = document.querySelectorAll('[data-is-modal-loaded]');
    modalTriggers.forEach(removeLoadedAttributeFromModalTrigger);
  }

  /**
   * Displays the thank you screen for users after form submission.
   * This function creates a modal for the thank you message by cloning
   * the thank you template. It binds necessary event listeners to the
   * modal and determines whether to display the mobile or desktop version
   * of the thank you screen based on the device type.
   *
   * @function showThankYouScreen
   * @returns {void}
  */
  const showThankYouScreen = () => {
    const thankYouModal = getModal(thankYouTemplate);
    bindEvents(thankYouModal);
    document.body.classList.add('nhs-u-overflow--hidden');

    // Hide the create account button if the user is logged in
    if (isUserLoggedIn()) {
      const createAccountBtn = thankYouModal.querySelector('[data-create-account-button]');
      if (createAccountBtn) {
        createAccountBtn.hidden = true;
      }
    }

    if (isMobileDevice) {
      showMobileThankYouScreen(thankYouModal);
      return;
    }

    showDesktopThankYouScreen(thankYouModal);
  }

  /**
   * Retrieves the form data from the mortgage lead form.
   * This function collects values from various input fields within the form
   * and returns an object containing the lead action, lead type, email address,
   * move date, name, phone number, and postal code.
   *
   * @function getFormData
   * @param {HTMLElement} form - The form element containing the input fields.
   * @returns {Object} An object containing the form data with the following properties:
   *   - leadAction: The action associated with the lead.
   *   - leadType: The type of lead being submitted.
   *   - mailAddress: The email address of the user.
   *   - moveDate: The date the user plans to move.
   *   - name: The name of the user.
   *   - userPhoneNumber: The user's phone number.
   *   - userPostalCode: The user's postal code.
   */
  const getFormData = (form) => {
    return {
      leadAction: form.querySelector('[data-lead-action-input]').value,
      leadType: form.querySelector('[data-lead-type-input]').value,
      mailAddress: form.querySelector('[data-email-input]').value,
      moveInDate: form.querySelector('[data-move-date-input]').value,
      name: form.querySelector('[data-name-input]').value,
      userPhoneNumber: form.querySelector('[data-phone-input]').value,
      userPostalCode: form.querySelector('[data-zip-code-input]').value,
      Guid: window.NewHomeSource.GuidGenerator.leadGuid,
      communityId: form.querySelector('[data-lead-community-id]').value,
      planId: form.querySelector('[data-lead-plan-id]').value,
      specId: form.querySelector('[data-lead-spec-id]').value,
      fromPage: form.querySelector('[data-lead-from-page]').value,
    }
  }

  /**
   * Submits the mortgage lead form to the server.
   * This function retrieves the form data from the mortgage modal,
   * creates a FormData object, and sends a POST request to the 
   * specified API endpoint. It does not handle the response from 
   * the server, and is intended to be called after form validation 
   * has been completed.
   *
   * @function submit
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {Promise<void>} A promise that resolves when the fetch request is initiated.
   */
  const submit = async (mortgageModal) => {
    const form = mortgageModal.querySelector('[data-mortgage-form]');
    const submitActionUrl = form.getAttribute('data-action-url');

    fetch(submitActionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(getFormData(form)),
    });
  }

  /**
   * trackLeadFormSubmitEvent: tracks the event when a mortgage lead form is submitted.
   * @param {string} moveDate - The move-in date selected by the user.
   */
  function trackLeadFormSubmitEvent(moveDate) {
    const googleAnalyticsEvent = {
      eventType: 'GA4Event',
      eventName: 'mortgage_lead',
      params: {
        button_name: 'unlock_my_offer',
        position: 'gallery_slideshow_area',
        move_in_date: moveDate,
      }
    };
    document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
      detail: { data: googleAnalyticsEvent }
    }));
  }

  /**
   * Submits the mortgage lead form after validating it.
   * This function disables the submit button to prevent multiple submissions,
   * validates the form using the global formValidator, and if valid,
   * submits the form to the server and shows a Thank You messsage.
   * 
   * @function submitForm
   * @param {HTMLElement} mortgageModal - The modal element containing the mortgage lead form.
   * @returns {Promise<void>} A promise that resolves when the form submission logic is complete.
   */
  const submitForm = async (mortgageModal) => {
    const form = mortgageModal.querySelector('[data-mortgage-form]');
    const submitButton = mortgageModal.querySelector('[data-mortgage-cta-button]');

    submitButton.disabled = true;
    const valid = await global.formValidator.validateForm(form, true);
    toggleErrorMessages(form);

    if (valid) {
      submit(mortgageModal);
      closeModal(mortgageModal);
      showThankYouScreen();      
      trackLeadFormSubmitEvent(getFormData(form).moveInDate);      
    }

    submitButton.disabled = false;
  }

  /**
   * Displays the mortgage lead form modal based on the device type.
   * It checks for the presence of a form validator before proceeding.
   * If the device is mobile, it shows the mobile version of the form;
   * otherwise, it shows the desktop version.
   * 
   * @function showLeadForm
   * @returns {void}
   */
  const showLeadForm = () => {
    if (!global.formValidator) {
      return;
    }

    const mortgageModal = getModal(leadFormTemplate);
    bindEvents(mortgageModal);
    document.body.classList.add('nhs-u-overflow--hidden');

    if (isMobileDevice) {
      showMobileForm(mortgageModal);
      return;
    }

    showDesktopForm(mortgageModal);
  }

  /**
   * @constructs NewHomeSource.MortgageLeadFormHandler
   */
  function MortgageLeadFormHandler() {
    window.addEventListener('message', (event) => {
      const type = event.data.type;
      const payload = event.data.payload;

      if (type === 'NativeAdClicked' && payload.isMortgageBannerAd) {
        showLeadForm();
      }
    });
  }

  /**
   * @public
   *
   * noConflict: Returns control of NewHomeSource.MortgageLeadFormHandler to the previous script.
   */
  MortgageLeadFormHandler.noConflict = function noConflict() {
    global.NewHomeSource.MortgageLeadFormHandler = previousMortgageLeadFormHandler;
    return MortgageLeadFormHandler;
  };

  global.NewHomeSource.MortgageLeadFormHandler = MortgageLeadFormHandler;

  MortgageLeadFormHandler();
}(window));